import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BellRing, Check, FileSearch } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { api } from './api'
import type { Page } from './types'
import { EmptyState, PageHeader, StatusPill, TableShell } from './ui'

interface Analytics {
  plays: number
  completedPlays: number
  onlineDevices: number
  offlineDevices: number
  topAssets: { assetId: string; plays: number }[]
}

interface Notification {
  id: string
  type: string
  title: string
  body: string
  read: boolean
  actionUrl: string | null
  createdAt: string
}

interface AuditRecord {
  id: string
  action: string
  resourceType: string
  resourceId: string | null
  actorUserId: string | null
  actorDeviceId: string | null
  requestId: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
}

export function AnalyticsPage() {
  const from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  const to = new Date()
  const overview = useQuery({
    queryKey: ['analytics', from.toISOString().slice(0, 10)],
    queryFn: () =>
      api<Analytics>(
        `/analytics/overview?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`
      ),
  })
  const completion = overview.data?.plays
    ? Math.round((overview.data.completedPlays / overview.data.plays) * 100)
    : 0
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Proof of play'
        title='Playback analytics'
        description='Raw idempotent events are retained for 90 days and rolled into daily aggregates for 24 months.'
      />
      <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
        {[
          ['30-day plays', overview.data?.plays ?? '—'],
          ['Completion rate', `${completion}%`],
          ['Online screens', overview.data?.onlineDevices ?? '—'],
          ['Offline screens', overview.data?.offlineDevices ?? '—'],
        ].map(([label, value]) => (
          <Card key={String(label)}>
            <CardContent className='p-5'>
              <p className='text-xs font-bold tracking-wider text-muted-foreground uppercase'>
                {label}
              </p>
              <p className='mt-3 text-3xl font-black'>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardContent className='p-6'>
          <h2 className='font-bold'>Top assets</h2>
          <div className='mt-5 space-y-4'>
            {overview.data?.topAssets.length ? (
              overview.data.topAssets.map((asset, index) => {
                const maximum = overview.data?.topAssets[0]?.plays ?? 1
                return (
                  <div key={asset.assetId}>
                    <div className='mb-2 flex justify-between text-xs'>
                      <span className='font-mono text-muted-foreground'>
                        {index + 1}. {asset.assetId.slice(0, 12)}
                      </span>
                      <span className='font-bold'>
                        {asset.plays.toLocaleString()} plays
                      </span>
                    </div>
                    <div className='h-2 overflow-hidden rounded-full bg-muted'>
                      <div
                        className='h-full rounded-full bg-primary'
                        style={{
                          width: `${Math.max(4, (asset.plays / maximum) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                )
              })
            ) : (
              <p className='text-sm text-muted-foreground'>
                Playback events will appear after players report their first
                completed batch.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function NotificationsPage() {
  const queryClient = useQueryClient()
  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<Notification[]>('/notifications'),
  })
  const read = useMutation({
    mutationFn: (id: string) =>
      api(`/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: async () =>
      queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Inbox'
        title='Notifications'
        description='Device health, failed processing, publication, payment, grace, invitation, and system notices.'
      />
      {notifications.data?.length ? (
        <div className='space-y-3'>
          {notifications.data.map((notification) => (
            <article
              key={notification.id}
              className={`flex gap-4 rounded-xl border p-4 ${notification.read ? 'bg-card' : 'border-primary/30 bg-primary/[.04]'}`}
            >
              <span className='grid size-10 shrink-0 place-items-center rounded-xl bg-muted'>
                <BellRing className='size-4' />
              </span>
              <div className='min-w-0 flex-1'>
                <div className='flex flex-wrap items-center gap-2'>
                  <h2 className='font-semibold'>{notification.title}</h2>
                  <StatusPill value={notification.type} tone='neutral' />
                </div>
                <p className='mt-2 text-sm leading-6 text-muted-foreground'>
                  {notification.body}
                </p>
                <p className='mt-2 text-xs text-muted-foreground'>
                  {new Date(notification.createdAt).toLocaleString()}
                </p>
              </div>
              {!notification.read && (
                <Button
                  size='icon'
                  variant='ghost'
                  onClick={() => read.mutate(notification.id)}
                  aria-label={`Mark ${notification.title} read`}
                >
                  <Check className='size-4' />
                </Button>
              )}
            </article>
          ))}
        </div>
      ) : (
        <EmptyState
          title='Inbox clear'
          description='Operational and billing notifications will appear here.'
        />
      )}
    </div>
  )
}

export function AuditPage() {
  const logs = useQuery({
    queryKey: ['audit'],
    queryFn: () => api<Page<AuditRecord>>('/audit-logs?limit=100'),
  })
  return (
    <div className='space-y-7'>
      <PageHeader
        eyebrow='Governance'
        title='Audit log'
        description='Security-sensitive tenant actions are immutable online for 12 months and carry actor, resource, request, and IP context.'
      />
      {logs.data?.items.length ? (
        <TableShell
          headers={['Action', 'Resource', 'Actor', 'Request', 'Time']}
        >
          {logs.data.items.map((log) => (
            <tr key={log.id}>
              <td className='px-4 py-3 font-semibold'>{log.action}</td>
              <td className='px-4 py-3'>
                <p>{log.resourceType}</p>
                <p className='mt-1 font-mono text-[10px] text-muted-foreground'>
                  {log.resourceId?.slice(0, 12) ?? '—'}
                </p>
              </td>
              <td className='px-4 py-3 font-mono text-xs text-muted-foreground'>
                {(log.actorUserId ?? log.actorDeviceId)?.slice(0, 8) ??
                  'system'}
              </td>
              <td className='px-4 py-3 font-mono text-xs text-muted-foreground'>
                {log.requestId?.slice(0, 12) ?? '—'}
              </td>
              <td className='px-4 py-3 text-muted-foreground'>
                {new Date(log.createdAt).toLocaleString()}
              </td>
            </tr>
          ))}
        </TableShell>
      ) : (
        <EmptyState
          title='No audit records'
          description='Actions such as invitations, pairing, publishing, billing, and platform changes will appear here.'
          action={<FileSearch className='mx-auto size-5' />}
        />
      )}
    </div>
  )
}
